import React from "react";
import Container from '@mui/material/Container';
import Grid from '@mui/material/Unstable_Grid2';
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import Typography from "@mui/material/Typography";

import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from '../../zeon-react-ui/auth/model';
import TextInput from "../../zeon-react-ui/input/TextInput";
import PasswordInput from "../../zeon-react-ui/input/PasswordInput";
import Button, { ButtonType } from "../../zeon-react-ui/button/Button";

const _API_PREFERENCES_ = "/api/system/prefs";
const _API_GET_PREFERENCES_ = `${_API_PREFERENCES_}/get-ldap-key`;
const _API_TEST_LDAP_ = `${_API_PREFERENCES_}/test-ldap`;
const _API_SET_PREFERENCES_ = `${_API_PREFERENCES_}/set-ldap-key`;

const _BLOCK_TITLE_ = "LDAP Coordinates";

const LDAPBlock = () => {
  const auth = useAuth();
  let navigate = useNavigate();
  let location = useLocation();
  let from = location.state?.from?.pathname || "/";

  const [allDisabled, setAllDisabled] = React.useState(false);
  const [srvError, setSrvError] = React.useState("");
  const [srvSuccess, setSrvSuccess] = React.useState("");
  const [loaded, setLoaded] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const [showLDAPPassword, setShowLDAPPassword] = React.useState(false);
  const handleClickShowLDAPPassword = () => setShowLDAPPassword((show) => !show);
  const handleMouseDownLDAPPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const [ldapHost, setLDAPHost] = React.useState("ldap://127.0.0.1:389");
  const handleLDAPHostChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLDAPHost(e.currentTarget.value);
    checkCanTest();
  };
  const [ldapBase, setLDAPBase] = React.useState("dc=example,dc=net");
  const handleLDAPBaseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLDAPBase(e.currentTarget.value);
    checkCanTest();
  };
  const [ldapBind, setLDAPBind] = React.useState("cn=admin,ou=users,dc=example,dc=net");
  const handleLDAPBindChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLDAPBind(e.currentTarget.value);
    checkCanTest();
  };
  const [ldapPassword, setLDAPPassword] = React.useState("password");
  const handleLDAPPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLDAPPassword(e.currentTarget.value);
    checkCanTest();
  };

  const handleCloseError = () => { setSrvError(""); }
  const handleCloseSuccess = () => { setSrvSuccess(""); }

  const [canApply, setCanApply] = React.useState(false);
  const [canTest, setCanTest] = React.useState(false);
  const checkCanTest = () => {
    setCanApply(false);
    setCanTest(Boolean(ldapBase && ldapHost && ldapBind && ldapPassword));
  };
  const handleRevertButton = (event: React.MouseEvent | null) => {
    event?.preventDefault();
    setLoading(true);
    setAllDisabled(true);
    axios.post(_API_GET_PREFERENCES_, { key: "aaa.ad" }).then((answer) => {
      setAllDisabled(false);
      setCanApply(false);
      if (answer?.data?.success) {
        const { dc, dn, base, pw } = answer.data.payload;
        setLDAPBase(base);
        setLDAPBind(dn);
        setLDAPHost(dc);
        setLDAPPassword(pw);
        setLoaded(true);
        setSrvSuccess("Configuration retrieved from server with success.");
        setTimeout(checkCanTest, 100);
      } else {
        checkCanTest();
        throw new Error("Cannot retrieve configuration from server!");
      };
    }).catch(e => {
      setAllDisabled(false);
      console.error(e);
      setSrvError(e.message);
    });
  };
  const handleTestButton = (event: React.MouseEvent | null) => {
    event?.preventDefault();
    setAllDisabled(true);
    const payload = { dc: ldapHost, dn: ldapBind, pw: ldapPassword, base: ldapBase }
    axios.post(_API_TEST_LDAP_, payload).then((answer) => {
      setAllDisabled(false);
      if (answer?.data?.success) {
        setCanApply(true);
        setSrvSuccess("Configuration tested and it is working right. You must click APPLY button to set it as persistent.");
      } else {
        throw new Error(answer?.data?.payload ? answer.data.payload : "Configuration not working!");
      };
    }).catch(e => {
      setAllDisabled(false);
      console.error(e);
      setSrvError(e.message);
    });
  };
  const handleApplyButton = (event: React.MouseEvent | null) => {
    event?.preventDefault();
    setAllDisabled(true);
    const payload = { dc: ldapHost, dn: ldapBind, pw: ldapPassword, base: ldapBase }
    axios.post(_API_SET_PREFERENCES_, { key: "aaa.ad", payload }).then((answer) => {
      setAllDisabled(false);
      if (answer?.data?.success) {
        setCanApply(false);
        setCanTest(false);
        setSrvSuccess("Configuration saved!");
      } else {
        throw new Error(answer?.data?.payload ? answer.data.payload : "An error has occured while saving configuration!");
      };
    }).catch(e => {
      setAllDisabled(false);
      setCanApply(false);
      console.error(e);
      setSrvError(e.message);
    });
  };

  if (!loaded && !loading) handleRevertButton(null);

  return (
    <Container sx={{ mb: 1 }}>
      <Grid container spacing={2} sx={{ borderColor: "primary.main", borderWidth: 1, borderStyle: "solid", p: 0, pr: 2, m: 0 }}>

        <Grid xs={12}>
          <Box sx={{ minWidth: 1, bgcolor: "primary.main", color: "white", p: 1 }}>
            <Typography><b>{_BLOCK_TITLE_}</b></Typography>
          </Box>
        </Grid>

        <Grid xs={12} sx={{ ml: 1, mr: 0 }}>
          <TextInput label="Host (FQDN or IP)" value={ldapHost} onChange={setLDAPHost} disabled={allDisabled} />
        </Grid>
        <Grid xs={12} sx={{ ml: 1, mr: 0 }}>
          <TextInput label="LDAP Base" value={ldapBase} onChange={setLDAPBase} disabled={allDisabled} />
        </Grid>
        <Grid xs={12} sx={{ ml: 1, mr: 0 }}>
          <TextInput label="Bind DN" value={ldapBind} onChange={setLDAPBind} disabled={allDisabled} />
        </Grid>
        <Grid xs={12} sx={{ ml: 1, mr: 0 }}>
          <PasswordInput label="Bind Password" value={ldapPassword} onChange={setLDAPPassword} disabled={allDisabled} />
        </Grid>

        <Grid xs={12}>
          <Box sx={{ textAlign: "right" }}>
            <Button label="Revert" type={ButtonType.cancel} onClick={handleRevertButton} disabled={allDisabled}/>
            &nbsp;
            <Button label="Test" type={ButtonType.normal} onClick={handleTestButton} disabled={!canTest || allDisabled}/>
            &nbsp;
            <Button label="Apply" type={ButtonType.default} onClick={handleApplyButton} disabled={!canApply || allDisabled}/>
          </Box>
        </Grid>

      </Grid>

      <Snackbar open={Boolean(srvError)} autoHideDuration={6000} onClose={handleCloseError}>
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          <b>{_BLOCK_TITLE_}</b><br />
          {srvError}
        </Alert>
      </Snackbar>
      <Snackbar open={Boolean(srvSuccess)} autoHideDuration={6000} onClose={handleCloseSuccess}>
        <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
          <b>{_BLOCK_TITLE_}</b><br />
          {srvSuccess}
        </Alert>
      </Snackbar>
    </Container >
  );
}

export default LDAPBlock;